<script lang="ts">
  import { Paperclip, Send, Square, MousePointer2, Mic, MicOff, Sparkles, SearchCheck } from "lucide-svelte";
  import { tick } from "svelte";
  import { getChatContext } from "../../chat/chat-runtime-context";
  import { i18n } from "../../chat/i18n.svelte";
  import { createVoiceRecognition, type VoiceRecognitionHandle } from "../../chat/voice-recognition";
  import type { SkillMeta, ToolCallPart } from "../../chat/types";
  import SkillPicker from "./SkillPicker.svelte";

  const skillIconMap = { Sparkles, SearchCheck } as const;
  function getSkillIcon(name: string | undefined) {
    return (name && (skillIconMap as any)[name]) || Sparkles;
  }

  const { t } = i18n;

  // Strip LLM-generated prefixes like "PA1 —", "Phương án 1 —", "Option A —", "🔧 PA1 —" etc.
  function cleanChipLabel(label: string): string {
    let s = String(label ?? "").trim();
    // 1. Strip leading decorative emoji/symbols
    s = s.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}🔧🔨⚙📌📋✅❌🎯💡⚡▶▪•·]+\s*/u, "");
    // 2. Strip prefix like "PA1 —", "PA 2 -", "Phương án 1 —", "Option A —", "Cách 1 —", "Phase 1 —", "1." or "1)"
    s = s.replace(
      /^(?:PA\s*\d+|Phương\s*án\s*\d+|Option\s+[A-Z]|Cách\s*\d+|Phase\s*\d+|\d+)\s*[—–\-:.)\]]+\s*/iu,
      ""
    );
    return s.trim() || String(label);
  }

  // Tailwind-inspired pastel palette: bg-{c}-50 / border-{c}-200 / text-{c}-700 / hover bg-{c}-100
  const CHIP_COLORS = [
    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", hover: "#dbeafe" }, // blue
    { bg: "#eef2ff", border: "#c7d2fe", text: "#4338ca", hover: "#e0e7ff" }, // indigo
    { bg: "#fffbeb", border: "#fde68a", text: "#b45309", hover: "#fef3c7" }, // amber
    { bg: "#f0fdfa", border: "#99f6e4", text: "#0f766e", hover: "#ccfbf1" }, // teal
    { bg: "#fdf2f8", border: "#fbcfe8", text: "#be185d", hover: "#fce7f3" }, // pink
  ];

  const LINE_HEIGHT = 20;
  const MIN_ROWS = 1;
  const MAX_ROWS = 4;

  const chat = getChatContext();
  const runtimeState = chat.state;

  let input = $state("");
  let textareaRef: HTMLTextAreaElement | null = null;
  let fileInputRef: HTMLInputElement | null = null;
  let isFocused = $state(false);

  // ─── Skill picker state ───────────────────────────────────────────
  let showSkillPicker = $state(false);
  /** Index of the trigger "/" in the textarea, or -1 if picker was opened via button. */
  let slashStart = $state(-1);
  /** Substring after the trigger / used for fuzzy filter. */
  let slashQuery = $state("");
  let skillPickerRef: SkillPicker | null = null;

  /**
   * Slash trigger rule: "/" is a valid trigger when it sits at position 0
   * or is preceded by whitespace. This avoids hijacking URLs (https://) and
   * path-like input mid-word.
   */
  function checkSlashTrigger() {
    if (!textareaRef) return;
    const caret = textareaRef.selectionStart ?? 0;
    const value = input;
    // Walk left from caret to find a "/" that may be a slash trigger.
    let i = caret - 1;
    let q = "";
    while (i >= 0) {
      const ch = value.charAt(i);
      if (ch === "/") {
        const prev = i === 0 ? "" : value.charAt(i - 1);
        if (prev === "" || /\s/.test(prev)) {
          slashStart = i;
          slashQuery = q;
          showSkillPicker = true;
        } else {
          showSkillPicker = false;
          slashStart = -1;
        }
        return;
      }
      if (/\s/.test(ch)) {
        // Hit whitespace before finding /, no active trigger.
        if (slashStart !== -1) {
          showSkillPicker = false;
          slashStart = -1;
        }
        return;
      }
      q = ch + q;
      i -= 1;
    }
    if (slashStart !== -1) {
      showSkillPicker = false;
      slashStart = -1;
    }
  }

  function selectSkill(skill: SkillMeta) {
    chat.setPendingSkill(skill);
    // If picker was opened via slash typed in textarea, strip the /query token.
    if (slashStart >= 0 && textareaRef) {
      const before = input.slice(0, slashStart);
      const afterToken = input.slice(slashStart + 1 + slashQuery.length);
      input = (before + afterToken).replace(/^\s+/, "");
      queueMicrotask(() => {
        autoResize();
        textareaRef?.focus();
        // restore caret to where the slash token used to start
        const pos = before.length;
        textareaRef?.setSelectionRange(pos, pos);
      });
    } else {
      textareaRef?.focus();
    }
    showSkillPicker = false;
    slashStart = -1;
    slashQuery = "";
  }

  function closeSkillPicker() {
    showSkillPicker = false;
    slashStart = -1;
    slashQuery = "";
  }

  function toggleSkillPickerButton() {
    if (showSkillPicker) {
      closeSkillPicker();
    } else {
      slashStart = -1;
      slashQuery = "";
      showSkillPicker = true;
      textareaRef?.focus();
    }
  }

  const pendingSelection = $derived(
    $runtimeState.messages
      .flatMap(m => m.parts)
      .find(p => p.type === "toolCall" && p.status === "awaiting_selection") as ToolCallPart | undefined
  );

  // ─── Voice recognition state ───────────────────────────────────────
  let isRecording = $state(false);
  let voiceHandle: VoiceRecognitionHandle | null = null;
  /** Text confirmed by the recogniser so far (accumulated across segments). */
  let confirmedText = $state("");
  /** Interim (in-flight) hypothesis while user is still speaking. */
  let interimText = $state("");

  /**
   * Resolve language tag from the i18n locale.
   * SpeechRecognition needs BCP-47, e.g. "vi-VN" or "en-US".
   */
  function voiceLang(): string {
    return i18n.locale === "vi" ? "vi-VN" : "en-US";
  }

  function initVoice() {
    voiceHandle = createVoiceRecognition(voiceLang(), {
      onInterimResult: (text) => {
        interimText = text;
        // Show confirmed + interim in the input box as a live preview.
        input = confirmedText + text;
        queueMicrotask(autoResize);
      },
      onFinalResult: (text) => {
        confirmedText += text;
        interimText = "";
        input = confirmedText;
        queueMicrotask(autoResize);
      },
      onError: (code) => {
        const key = errorCodeToI18nKey(code);
        runtimeState.update(s => ({ ...s, error: t(key as any) }));
        stopRecording();
      },
      onEnd: () => {
        isRecording = false;
      },
    });
  }

  function errorCodeToI18nKey(code: string): string {
    switch (code) {
      case "VOICE_ERROR_PERMISSION": return "voicePermissionDenied";
      case "VOICE_ERROR_NETWORK":    return "voiceNetworkError";
      case "VOICE_ERROR_MIC":        return "voiceMicError";
      default:                       return "voiceGenericError";
    }
  }

  function toggleRecording() {
    if (!voiceHandle) initVoice();
    if (!voiceHandle) return;

    if (!voiceHandle.isSupported) {
      runtimeState.update(s => ({ ...s, error: t("voiceNotSupported") }));
      return;
    }

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }

  async function startRecording() {
    if (!voiceHandle) return;
    // Reset accumulators but keep anything already typed.
    confirmedText = input;
    interimText = "";
    isRecording = true;
    await voiceHandle.start();
  }

  function stopRecording() {
    if (!voiceHandle) return;
    voiceHandle.stop();
    isRecording = false;
    interimText = "";
    // Focus the textarea so the user can review / edit before sending.
    textareaRef?.focus();
  }

  // ─── Original functionality ────────────────────────────────────────

  async function handleFileChange() {
    if (!fileInputRef || !fileInputRef.files || fileInputRef.files.length === 0) return;
    const files = Array.from(fileInputRef.files);
    fileInputRef.value = "";
    for (const file of files) {
      await chat.handleUpload(file);
    }
  }

  function autoResize() {
    if (!textareaRef) return;
    textareaRef.style.height = "auto";
    const min = LINE_HEIGHT * MIN_ROWS;
    const max = LINE_HEIGHT * MAX_ROWS;
    const clamped = Math.max(min, Math.min(textareaRef.scrollHeight, max));
    textareaRef.style.height = `${clamped}px`;
    textareaRef.style.overflowY = textareaRef.scrollHeight > max ? "auto" : "hidden";
  }

  async function handleSubmit() {
    // Stop recording first if active.
    if (isRecording) stopRecording();

    const trimmed = input.trim();
    const skill = $runtimeState.pendingSkill;
    // Allow send with empty input when a skill is armed — use the skill's
    // localized name as the user-visible message so chat history is readable.
    if (!trimmed && !skill) return;
    if ($runtimeState.isStreaming) return;

    const text = trimmed || (skill ? skill.name[i18n.locale] : "");
    input = "";
    confirmedText = "";
    interimText = "";
    await tick();
    await chat.sendMessage(text);
  }

  const canSend = $derived(
    (input.trim().length > 0 || $runtimeState.pendingSkill !== null) &&
    !$runtimeState.isUploading
  );

  $effect(() => {
    input;
    queueMicrotask(autoResize);
  });

  function translateProgressMessage(msg: string | undefined): string {
    if (!msg) return "";
    if (i18n.locale === "en") return msg;

    if (msg === "Analyzing file type...") return "Đang phân tích loại tệp...";
    if (msg === "Reading PDF content...") return "Đang đọc nội dung PDF...";
    if (msg.startsWith("Processing ") && msg.endsWith(" page(s) with vision AI...")) {
      return msg.replace("Processing ", "Đang xử lý ").replace(" page(s) with vision AI...", " trang với AI thị giác...");
    }
    if (msg.startsWith("Reading page ")) {
      return msg.replace("Reading page ", "Đang đọc trang ").replace(" of ", "/");
    }
    if (msg === "Reading spreadsheet data...") return "Đang đọc dữ liệu bảng tính...";
    if (msg === "Preparing image for OCR...") return "Đang chuẩn bị hình ảnh để nhận dạng...";
    if (msg === "Reading document with AI...") return "Đang đọc tài liệu bằng AI...";
    if (msg === "Structuring data...") return "Đang cấu trúc dữ liệu...";
    if (msg === "Initiating upload...") return "Đang khởi tạo tải lên...";
    if (msg === "Massive data detected. Importing...") return "Phát hiện dữ liệu lớn. Đang nhập...";
    
    return msg;
  }
</script>

<div style="
  flex-shrink: 0;
  padding: 6px 10px 6px;
  background: var(--chat-bg);
  border-top: 1px solid var(--chat-bg-tertiary);
">
  {#if $runtimeState.error}
    <div style="
      padding: 6px 10px; margin-bottom: 6px;
      background: rgba(241,112,122,0.10);
      border: 1px solid rgba(241,112,122,0.25);
      border-radius: var(--chat-radius);
      font-size: 11px; color: var(--chat-error);
      font-family: var(--chat-font-sans);
    ">
      {$runtimeState.error}
    </div>
  {/if}

  {#if $runtimeState.currentSelection || $runtimeState.uploads.length > 0 || $runtimeState.isUploading}
    <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px;">
      {#if $runtimeState.currentSelection}
        <div class="fluent-fade-in" style="
          display: flex; align-items: center; gap: 6px;
          padding: 4px 10px;
          background: var(--chat-accent-subtle);
          border: 1px solid var(--chat-accent);
          border-radius: 6px;
          font-size: 11.5px; color: var(--chat-accent);
          font-family: var(--chat-font-sans);
          min-width: 0;
          cursor: help;
        " title={`Selection in ${$runtimeState.currentSelection.sheetName}`}>
          <MousePointer2 size={12} />
          <span style="font-weight: 700; white-space: nowrap;">
            {$runtimeState.currentSelection.address}
          </span>
          <span style="font-size: 10px; opacity: 0.7; font-weight: 500;">
            {$runtimeState.currentSelection.sheetName}
          </span>
        </div>
      {/if}

      {#each $runtimeState.uploads as file, idx (file.id ? `inp-${file.id}-${idx}` : `inp-up-${idx}`)}
        <div style="
          display: flex; align-items: center; gap: 6px;
          padding: 4px 10px;
          background: var(--chat-bg-tertiary);
          border: 1px solid var(--chat-border);
          border-radius: 6px;
          font-size: 11.5px; color: var(--chat-text-secondary);
          font-family: var(--chat-font-sans);
          min-width: 0;
        ">
          <span style="max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500;" title={file.name}>{file.name}</span>
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            onclick={() => {
              runtimeState.update(s => ({ ...s, uploads: s.uploads.filter(u => u.id !== file.id || (u.id === undefined && u.name !== file.name)) }));
            }}
            style="color: var(--chat-text-muted); background: none; border: none; padding: 2px; cursor: pointer; display: flex;"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      {/each}

      {#if $runtimeState.isUploading && $runtimeState.uploadProgress}
        <div style="
          display: flex; flex-direction: column; gap: 4px;
          padding: 6px 12px;
          background: var(--chat-bg-tertiary);
          border: 1px solid var(--chat-accent);
          border-radius: 6px;
          min-width: 180px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        ">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
            <span style="font-size: 11.5px; font-weight: 600; color: var(--chat-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 120px;">
                {$runtimeState.uploadingFileName ?? t("uploading")}
            </span>
            <span style="font-size: 10px; font-weight: 700; color: var(--chat-accent);">
                {$runtimeState.uploadProgress.pct}%
            </span>
          </div>
          <div style="font-size: 9.5px; color: var(--chat-text-muted); margin-bottom: 2px;">
            {translateProgressMessage($runtimeState.uploadProgress.message)}
          </div>
          <div style="width: 100%; height: 3px; background: rgba(0,0,0,0.05); border-radius: 99px; overflow: hidden;">
            <div style="width: {$runtimeState.uploadProgress.pct}%; height: 100%; background: var(--chat-accent); border-radius: 99px; transition: width 0.3s ease;"></div>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Listening indicator -->
  {#if isRecording}
    <div class="fluent-fade-in" style="
      display: flex; align-items: center; gap: 6px;
      margin-bottom: 6px;
      padding: 4px 10px;
      border-radius: 6px;
      background: rgba(220, 38, 38, 0.06);
      border: 1px solid rgba(220, 38, 38, 0.18);
    ">
      <span style="
        width: 6px; height: 6px;
        border-radius: 50%;
        background: #dc2626;
        animation: pulse 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      "></span>
      <span style="
        font-size: 11px;
        font-family: var(--chat-font-sans);
        color: #dc2626;
        font-weight: 600;
        letter-spacing: 0.01em;
      ">
        {t("voiceListening")}
      </span>
    </div>
  {/if}

  <!-- Input container -->
  <div style="
    position: relative;
    display: flex; flex-direction: column;
    background: var(--chat-input-bg);
    border: 1px solid {isFocused || isRecording ? 'var(--chat-accent)' : 'var(--chat-bg-tertiary)'};
    border-radius: var(--chat-radius);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-shadow: {isFocused ? '0 0 0 2px var(--chat-accent-subtle)' : 'var(--chat-shadow-sm)'};
  ">
    <SkillPicker
      bind:this={skillPickerRef}
      open={showSkillPicker}
      skills={$runtimeState.skills}
      query={slashQuery}
      onSelect={selectSkill}
      onClose={closeSkillPicker}
    />
    <!-- Quick-reply chips: inside input container, above textarea, wraps to multiple lines -->
    {#if pendingSelection}
      {@const options = ((pendingSelection.args as any)?.options ?? []) as string[]}
      <div
        class="fluent-fade-in"
        style="
          display: flex; flex-wrap: wrap; align-items: center; gap: 4px;
          padding: 4px 8px;
          border-bottom: 1px solid var(--chat-bg-tertiary);
        "
      >
        {#each options as option, i}
          {@const c = CHIP_COLORS[i % CHIP_COLORS.length]}
          {@const label = cleanChipLabel(option)}
          <button
            type="button"
            onclick={() => chat.selectOption(pendingSelection!.id!, label)}
            title={label}
            style="
              max-width: 100%;
              padding: 3px 10px;
              border-radius: 9999px;
              background: {c.bg};
              border: 1px solid {c.border};
              color: {c.text};
              font-size: 11.5px; font-weight: 500;
              cursor: pointer;
              transition: background 0.12s, transform 0.1s;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              font-family: var(--chat-font-sans);
              box-shadow: 0 1px 2px rgba(0,0,0,0.04);
            "
            onmouseenter={(e) => { (e.currentTarget as HTMLElement).style.background = c.hover; }}
            onmouseleave={(e) => { (e.currentTarget as HTMLElement).style.background = c.bg; }}
            onmousedown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(var(--button-active-scale))'; }}
            onmouseup={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
          >{label}</button>
        {/each}
      </div>
    {/if}

    {#if $runtimeState.pendingSkill}
      {@const sk = $runtimeState.pendingSkill}
      {@const SkIcon = getSkillIcon(sk.icon)}
      <div
        class="fluent-fade-in"
        style="
          display: flex; align-items: center; gap: 6px;
          padding: 4px 8px;
          border-bottom: 1px solid var(--chat-bg-tertiary);
        "
      >
        <div style="
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 4px 3px 8px;
          background: var(--chat-accent-subtle);
          border: 1px solid var(--chat-accent);
          border-radius: 9999px;
          font-size: 11px;
          color: var(--chat-accent);
          font-weight: 600;
        ">
          <SkIcon size={12} />
          <span>{sk.name[i18n.locale]}</span>
          <span style="
            opacity: 0.75;
            font-weight: 500;
            font-size: 9.5px;
            padding: 0 4px;
            border-left: 1px solid currentColor;
            margin-left: 2px;
          ">{t("skillsActiveBadge")}</span>
          <button
            type="button"
            aria-label={t("skillsRemove")}
            title={t("skillsRemove")}
            onclick={() => chat.setPendingSkill(null)}
            style="
              display: inline-flex; align-items: center; justify-content: center;
              width: 16px; height: 16px;
              border: none; background: transparent;
              color: currentColor;
              cursor: pointer; opacity: 0.7;
              padding: 0;
            "
            onmouseenter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
            onmouseleave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.7')}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    {/if}

    <textarea
      bind:this={textareaRef}
      bind:value={input}
      disabled={$runtimeState.isUploading}
      oninput={() => { autoResize(); checkSlashTrigger(); }}
      onclick={checkSlashTrigger}
      onkeyup={(event) => {
        if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
          checkSlashTrigger();
        }
      }}
      onfocus={() => (isFocused = true)}
      onblur={() => (isFocused = false)}
      onkeydown={(event) => {
        // Let the skill picker capture arrow keys / Enter / Escape first.
        if (showSkillPicker && skillPickerRef?.handleKeydown(event)) return;

        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          void handleSubmit();
        }
      }}
      placeholder={$runtimeState.isUploading ? t("processingFile") : t("inputPlaceholder")}
      style="
        width: 100%; resize: none;
        background: transparent;
        color: var(--chat-text-primary);
        font-size: 12px;
        font-family: var(--chat-font-sans);
        padding: 5px 10px 0px;
        border: none; outline: none;
        line-height: {LINE_HEIGHT}px;
        height: {LINE_HEIGHT * MIN_ROWS}px;
        box-sizing: border-box;
      "
    ></textarea>

    <!-- Action bar -->
    <div style="display: flex; align-items: center; justify-content: space-between; padding: 0 4px 3px;">
      <!-- Left actions: Attach + Voice -->
      <div style="display: flex; align-items: center; gap: 2px;">
        <!-- Attach file -->
        <input
          type="file"
          multiple
          bind:this={fileInputRef}
          onchange={handleFileChange}
          style="display: none;"
          accept="image/*,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        />
        <button
          type="button"
          onclick={() => fileInputRef?.click()}
          disabled={$runtimeState.isUploading}
          style="
            width: 24px; height: 24px;
            display: flex; align-items: center; justify-content: center;
            border-radius: var(--chat-radius);
            color: {$runtimeState.isUploading ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};
            opacity: {$runtimeState.isUploading ? '0.7' : '1'};
            cursor: {$runtimeState.isUploading ? 'wait' : 'pointer'};
            background: none; border: none;
            transition: background 0.15s, color 0.15s;
          "
          onmouseenter={(e) => { if (!$runtimeState.isUploading) (e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-tertiary)'; }}
          onmouseleave={(e) => { if (!$runtimeState.isUploading) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <Paperclip size={14} class={$runtimeState.isUploading ? "animate-pulse" : ""} />
        </button>

        <!-- Skills -->
        <button
          type="button"
          title={t("skills")}
          onclick={toggleSkillPickerButton}
          disabled={$runtimeState.isStreaming || $runtimeState.isUploading}
          style="
            width: 24px; height: 24px;
            display: flex; align-items: center; justify-content: center;
            border-radius: var(--chat-radius);
            color: {showSkillPicker || $runtimeState.pendingSkill ? 'var(--chat-accent)' : 'var(--chat-text-muted)'};
            background: {showSkillPicker ? 'var(--chat-bg-tertiary)' : 'none'};
            border: none;
            cursor: {$runtimeState.isStreaming ? 'not-allowed' : 'pointer'};
            transition: background 0.15s, color 0.15s, transform 0.1s;
          "
          onmouseenter={(e) => { if (!$runtimeState.isStreaming) (e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-tertiary)'; }}
          onmouseleave={(e) => { if (!showSkillPicker) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          onmousedown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(var(--button-active-scale))'; }}
          onmouseup={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
        >
          <Sparkles size={14} />
        </button>

        <!-- Voice / Mic -->
        <button
          type="button"
          title={isRecording ? t("stop") : t("voiceListening")}
          onclick={toggleRecording}
          disabled={$runtimeState.isStreaming || $runtimeState.isUploading}
          class={isRecording ? "voice-recording" : ""}
          style="
            width: 24px; height: 24px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 50%;
            color: {isRecording ? '#ffffff' : 'var(--chat-text-muted)'};
            background: {isRecording ? '#dc2626' : 'none'};
            border: none;
            cursor: {$runtimeState.isStreaming ? 'not-allowed' : 'pointer'};
            transition: background 0.2s, color 0.2s, transform 0.1s;
          "
          onmouseenter={(e) => { if (!isRecording && !$runtimeState.isStreaming) (e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-tertiary)'; }}
          onmouseleave={(e) => { if (!isRecording) (e.currentTarget as HTMLElement).style.background = isRecording ? '#dc2626' : 'transparent'; }}
          onmousedown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(var(--button-active-scale))'; }}
          onmouseup={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
        >
          {#if isRecording}
            <MicOff size={14} />
          {:else}
            <Mic size={14} />
          {/if}
        </button>
      </div>

      <!-- Send / Stop -->
      {#if $runtimeState.isStreaming}
        <button
          type="button"
          title={t("stop")}
          onclick={() => chat.abort()}
          style="
            width: 26px; height: 26px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 50%;
            background: rgba(200, 32, 20, 0.1);
            color: var(--chat-error);
            border: none;
            cursor: pointer;
            transition: transform 0.1s;
          "
          onmousedown={(e) => ((e.currentTarget as HTMLElement).style.transform = 'scale(var(--button-active-scale))')}
          onmouseup={(e) => ((e.currentTarget as HTMLElement).style.transform = 'scale(1)')}
        >
          <Square size={12} fill="currentColor" />
        </button>
      {:else}
        <button
          type="button"
          title={t("send")}
          onclick={handleSubmit}
          disabled={!canSend}
          style="
            width: 26px; height: 26px;
            display: flex; align-items: center; justify-content: center;
            border-radius: 50%;
            background: {canSend ? 'var(--chat-accent)' : 'var(--chat-bg-tertiary)'};
            color: {canSend ? '#ffffff' : 'var(--chat-text-muted)'};
            border: none; cursor: {canSend ? 'pointer' : 'not-allowed'};
            transition: transform 0.1s, background 0.15s;
          "
          onmousedown={(e) => { if (canSend) (e.currentTarget as HTMLElement).style.transform = 'scale(var(--button-active-scale))'; }}
          onmouseup={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
        >
          <Send size={15} />
        </button>
      {/if}
    </div>
  </div>
</div>